import { useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Icon, Logo } from '@vault/ui';
import { useT } from '../i18n/index.js';
import { CenterShell } from './shared/CenterShell.js';

const ONBOARDED_KEY = 'tv.onboarded';
const WEBSTORE_URL = 'https://chromewebstore.google.com/';

/**
 * Three-step welcome: what Nomad Tabs is → why the browser will ask for access
 * (framed honestly and reassuringly) → rescue your tabs now. Finishing (any
 * path into the app) sets `tv.onboarded` so verify never sends you here again.
 */
export function OnboardingPage() {
  const navigate = useNavigate();
  const { t } = useT();
  const [step, setStep] = useState(0);

  function finish(to: string) {
    localStorage.setItem(ONBOARDED_KEY, '1');
    navigate(to, { replace: true });
  }

  return (
    <CenterShell maxWidth={620} glow={step === 2 ? 'amber' : 'primary'} align="center">
      <style>{ONBOARDING_CSS}</style>
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Dots step={step} />
        <button type="button" className="tvob-skip" onClick={() => finish('/timeline')}>
          {t('common.skip')}
        </button>

        {step === 0 && <Welcome onNext={() => setStep(1)} />}
        {step === 1 && <Permissions onNext={() => setStep(2)} onLater={() => setStep(2)} />}
        {step === 2 && (
          <Rescue
            onImport={() => finish('/import')}
            onOpen={() => finish('/timeline')}
          />
        )}
      </div>
    </CenterShell>
  );
}

function Dots({ step }: { step: number }) {
  return (
    <span style={{ display: 'flex', justifyContent: 'center', gap: 7, marginBottom: 34 }}>
      {[0, 1, 2].map((i) => (
        <i
          key={i}
          style={{
            width: i === step ? 22 : 8,
            height: 5,
            borderRadius: 3,
            background: i === step ? 'var(--primary)' : i < step ? 'var(--success)' : 'var(--surface-3)',
            transition: 'width .2s var(--ease)',
          }}
        />
      ))}
    </span>
  );
}

function Welcome({ onNext }: { onNext: () => void }) {
  const { t } = useT();
  return (
    <div style={centerCol}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 30 }}>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <span style={{ ...ghostTab, opacity: 0.4, transform: 'rotate(-4deg) translateX(-8px)' }} />
          <span style={{ ...ghostTab, opacity: 0.6, transform: 'rotate(2deg)' }} />
          <span style={{ ...ghostTab, opacity: 0.85, transform: 'rotate(-2deg) translateX(6px)' }} />
        </span>
        <Icon name="chevron-right" size={20} color="var(--text-3)" />
        <Logo size={84} style={{ borderRadius: 24, boxShadow: 'var(--glow-amber)' }} />
      </div>
      <h1 style={hero}>
        {t('onboarding.hero.title1')}
        <br />
        {t('onboarding.hero.title2')}
      </h1>
      <p style={lede}>
        {t('onboarding.hero.body')}
      </p>
      <div style={{ marginTop: 30 }}>
        <Button size="lg" onClick={onNext} icon={<Icon name="chevron-right" size={14} color="currentColor" />}>
          {t('onboarding.getStarted')}
        </Button>
      </div>
      <span style={microcopy}>{t('onboarding.hero.micro')}</span>
    </div>
  );
}

function Permissions({ onNext, onLater }: { onNext: () => void; onLater: () => void }) {
  const { t } = useT();
  const points: Array<[string, string]> = [
    [t('onboarding.perm.p1.title'), t('onboarding.perm.p1.body')],
    [t('onboarding.perm.p2.title'), t('onboarding.perm.p2.body')],
    [t('onboarding.perm.p3.title'), t('onboarding.perm.p3.body')],
  ];
  return (
    <div style={centerCol}>
      <span style={iconTile}>
        <Icon name="lock" size={26} color="var(--primary)" />
      </span>
      <h1 style={{ ...hero, fontSize: 28, marginTop: 18 }}>{t('onboarding.perm.title')}</h1>
      <p style={{ ...lede, marginTop: 8 }}>{t('onboarding.perm.lede')}</p>

      <div style={translateCard}>
        <div style={translateHead}>{t('onboarding.perm.head')}</div>
        {points.map(([h, d], i) => (
          <div key={h} style={{ ...translateRow, borderBottom: i < points.length - 1 ? '1px solid var(--line)' : 'none' }}>
            <span style={checkTile}>
              <Icon name="check" size={12} color="var(--success)" strokeWidth={2.2} />
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 13.5, fontWeight: 600 }}>{h}</span>
              <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{d}</span>
            </span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
        <Button size="lg" onClick={onNext}>
          {t('onboarding.perm.continue')}
        </Button>
        <Button size="lg" variant="ghost" onClick={onLater}>
          {t('onboarding.perm.later')}
        </Button>
      </div>
      <span style={microcopy}>{t('onboarding.perm.micro')}</span>
    </div>
  );
}

function Rescue({ onImport, onOpen }: { onImport: () => void; onOpen: () => void }) {
  const { t } = useT();
  return (
    <div style={centerCol}>
      <h1 style={{ ...hero, fontSize: 28 }}>{t('onboarding.rescue.title')}</h1>
      <p style={{ ...lede, marginTop: 8 }}>{t('onboarding.rescue.lede')}</p>

      <div className="tvob-cards" style={{ display: 'flex', gap: 16, marginTop: 26 }}>
        <div className="tvob-card" style={rescueCard} onClick={onImport}>
          <span style={{ ...cardMark, background: 'var(--fav-6)' }}>O</span>
          <span style={cardTitle}>{t('onboarding.rescue.onetab.title')}</span>
          <span style={cardBody}>
            {t('onboarding.rescue.onetab.body')}
          </span>
          <span style={{ marginTop: 'auto' }}>
            <Button variant="ghost" fullWidth>
              {t('onboarding.rescue.chooseFile')}
            </Button>
          </span>
          <span style={cardFoot}>{t('onboarding.rescue.onetab.foot')}</span>
        </div>

        <div className="tvob-card" style={{ ...rescueCard, ...rescueCardHero }}>
          <span style={{ ...cardMark, background: 'var(--primary)' }}>
            <Icon name="download" size={19} color="#fff" />
          </span>
          <span style={cardTitle}>{t('onboarding.rescue.ext.title')}</span>
          <span style={cardBody}>
            {t('onboarding.rescue.ext.body')}
          </span>
          <a href={WEBSTORE_URL} target="_blank" rel="noopener noreferrer" style={{ marginTop: 'auto' }}>
            <Button fullWidth icon={<Icon name="external" size={13} color="currentColor" />}>
              {t('common.addToChrome')}
            </Button>
          </a>
          <span style={cardFoot}>{t('onboarding.rescue.ext.foot')}</span>
        </div>
      </div>

      <button type="button" className="tvob-explore" onClick={onOpen}>
        {t('onboarding.rescue.openApp')}
      </button>
    </div>
  );
}

const centerCol: CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' };
const hero: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-display)', fontSize: 34, fontWeight: 600, letterSpacing: '-0.025em',
  lineHeight: 1.15,
  maxWidth: 560,
};
const lede: CSSProperties = { fontSize: 15, lineHeight: 1.6, color: 'var(--text-2)', maxWidth: 440, marginTop: 14 };
const microcopy: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '.12em',
  color: 'var(--text-3)',
  marginTop: 18,
};
const ghostTab: CSSProperties = { width: 104, height: 15, borderRadius: 5, background: 'var(--surface-3)' };
const iconTile: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 64,
  height: 64,
  borderRadius: 20,
  background: 'var(--primary-tint)',
  border: '1px solid var(--line-primary)',
};
const translateCard: CSSProperties = {
  width: '100%',
  maxWidth: 520,
  background: 'var(--surface-1)',
  border: '1px solid var(--line)',
  borderRadius: 16,
  boxShadow: 'var(--shadow-card)',
  marginTop: 24,
  overflow: 'hidden',
  textAlign: 'left',
};
const translateHead: CSSProperties = {
  padding: '12px 18px',
  borderBottom: '1px solid var(--line)',
  background: 'var(--surface-0)',
  fontFamily: 'var(--font-mono)',
  fontSize: 9.5,
  letterSpacing: '.14em',
  color: 'var(--text-3)',
};
const translateRow: CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '13px 18px' };
const checkTile: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 22,
  borderRadius: 7,
  background: 'var(--success-tint)',
  flexShrink: 0,
};
const rescueCard: CSSProperties = {
  width: 280,
  minHeight: 210,
  background: 'var(--surface-1)',
  border: '1px solid var(--line)',
  borderRadius: 16,
  boxShadow: 'var(--shadow-card)',
  padding: 22,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  cursor: 'pointer',
  textAlign: 'left',
};
const rescueCardHero: CSSProperties = {
  background: 'linear-gradient(160deg, rgba(24,24,27,.04), transparent 55%), var(--surface-1)',
  border: '1px solid var(--line-primary)',
  boxShadow: 'var(--glow-primary)',
};
const cardMark: CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 12,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 17,
  fontWeight: 700,
  color: '#fff',
};
const cardTitle: CSSProperties = { fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 600, letterSpacing: '-.01em' };
const cardBody: CSSProperties = { fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-2)' };
const cardFoot: CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-3)', textAlign: 'center' };

const ONBOARDING_CSS = `
.tvob-skip{position:absolute;top:-6px;inset-inline-end:0;background:transparent;border:none;font-family:var(--font-body);
  font-size:12px;font-weight:600;color:var(--text-3);cursor:pointer;padding:4px 6px}
.tvob-skip:hover{color:var(--text-1)}
.tvob-card{transition:border-color .15s var(--ease),transform .15s var(--ease)}
.tvob-card:hover{border-color:var(--line-strong);transform:translateY(-2px)}
.tvob-explore{margin-top:24px;background:transparent;border:none;font-family:var(--font-body);font-size:12.5px;
  font-weight:600;color:var(--text-2);cursor:pointer}
.tvob-explore:hover{color:var(--text-1)}
@media (max-width:600px){.tvob-cards{flex-direction:column;align-items:center}}
`;
